import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize } from 'rxjs';
import { Httpservice } from '../../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { Commonservice } from '../../../services/commonservice';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { AuditLogChangeItem, AuditLogDetail, AuditLogItem, AuditLogsPagination } from './audit-logs.interfaces';

const AUDIT_LOGS_NO_RECORDS_LABEL = 'No records';
const AUDIT_LOGS_SHOWING_RESULTS_LABEL = 'Showing';
const AUDIT_LOGS_RESULTS_LABEL = 'results';
const AUDIT_LOGS_FAILED_LOAD_MESSAGE = 'Failed to load audit logs:';
const AUDIT_LOGS_FAILED_EXPORT_MESSAGE = 'Failed to export audit logs.';
const AUDIT_LOGS_FAILED_DOWNLOAD_MESSAGE = 'Failed to download full log.';
const AUDIT_LOGS_FAILED_DETAIL_MESSAGE = 'Failed to load audit log detail.';
const AUDIT_LOGS_ALL_FILTER_VALUE = 'all';
const AUDIT_LOGS_FETCH_FILTER_VALUE = 'fetch';
const AUDIT_LOGS_ADD_FILTER_VALUE = 'add';
const AUDIT_LOGS_EDIT_FILTER_VALUE = 'edit';
const AUDIT_LOGS_DELETE_FILTER_VALUE = 'delete';

type AuditLogsQueryValue = string | number | null | undefined;
type AuditLogsQueryParams = Record<string, AuditLogsQueryValue>;

type AuditLogsEventFilterValue =
  | typeof AUDIT_LOGS_ALL_FILTER_VALUE
  | typeof AUDIT_LOGS_FETCH_FILTER_VALUE
  | typeof AUDIT_LOGS_ADD_FILTER_VALUE
  | typeof AUDIT_LOGS_EDIT_FILTER_VALUE
  | typeof AUDIT_LOGS_DELETE_FILTER_VALUE;

type AuditLogCellValue = number | string | null | undefined;

interface AuditLogFilterOption {
  value: AuditLogsEventFilterValue;
  label: string;
}

@Component({
  selector: 'app-audit-logs-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent],
  templateUrl: './audit-logs.html',
  styleUrl: './audit-logs.scss',
})
export class AuditLogsPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly eventOptions: AuditLogFilterOption[] = [
    { value: AUDIT_LOGS_ALL_FILTER_VALUE, label: 'All Events' },
    { value: AUDIT_LOGS_FETCH_FILTER_VALUE, label: 'Fetch' },
    { value: AUDIT_LOGS_ADD_FILTER_VALUE, label: 'Add' },
    { value: AUDIT_LOGS_EDIT_FILTER_VALUE, label: 'Edit' },
    { value: AUDIT_LOGS_DELETE_FILTER_VALUE, label: 'Delete' },
  ];

  protected readonly filterForm: UntypedFormGroup;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl: FormControl;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected downloadingLogId: number | null = null;
  protected isDetailLoading = false;
  protected isDetailModalOpen = false;
  protected selectedLogDetail: AuditLogDetail | null = null;
  protected selectedLogChanges: AuditLogChangeItem[] = [];
  protected selectedLogItem: AuditLogItem | null = null;
  protected detailModalErrorMessage = '';

  protected logs: AuditLogItem[] = [];
  protected filteredLogs: AuditLogItem[] = [];
  protected pagination: AuditLogsPagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly commonSvc: Commonservice,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
      module_name: [''],
      event: [AUDIT_LOGS_ALL_FILTER_VALUE],
      from_date: [''],
      to_date: [''],
      page_size: [15],
    });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;
  }

  ngOnInit(): void {
    this.loadAuditLogs(1);

    this.filterForm.valueChanges
      .pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadAuditLogs(1));
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected loadAuditLogs(pageNo = this.pagination.page_no): void {
    this.isLoading = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      module_name: this.filterForm.get('module_name')?.value,
      event: this.filterForm.get('event')?.value,
      from_date: this.filterForm.get('from_date')?.value,
      to_date: this.filterForm.get('to_date')?.value,
      page_no: pageNo,
      page_size: this.filterForm.get('page_size')?.value,
    });

    this.httpSvc
      .requestCall(AuthEndPoints.GET_ACTIVITY_LOGS, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          this.logs = res?.value?.activity_logs ?? res?.activity_logs ?? [];
          this.filteredLogs = [...this.logs];
          this.pagination = this.extractPagination(res) ?? this.extractPagination(res?.value) ?? this.pagination;
        },
        error: (err) => {
          console.error(AUDIT_LOGS_FAILED_LOAD_MESSAGE, err);
          this.logs = [];
          this.filteredLogs = [];
          this.pagination = {
            page_no: pageNo,
            page_size: Number(this.filterForm.get('page_size')?.value ?? 10),
            total_records: 0,
            total_pages: 0,
          };
        },
      });
  }

  protected handleExportAuditLogs(): void {
    if (this.isLoading || this.isExporting) {
      return;
    }

    this.isExporting = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      module_name: this.filterForm.get('module_name')?.value,
      event: this.filterForm.get('event')?.value,
      from_date: this.filterForm.get('from_date')?.value,
      to_date: this.filterForm.get('to_date')?.value,
      page_no: this.pagination.page_no,
      page_size: this.pagination.page_size,
    });

    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_ACTIVITY_LOGS, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildAuditLogsFileName()),
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || AUDIT_LOGS_FAILED_EXPORT_MESSAGE),
      });
  }

  protected downloadFullLog(item: AuditLogItem): void {
    if (this.downloadingLogId !== null) {
      return;
    }

    this.downloadingLogId = item.id;
    this.httpSvc
      .downloadfile(AuthEndPoints.DOWNLOAD_ACTIVITY_LOG_FULL, ApiMethod.GET, `?id=${item.id}`)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.downloadingLogId = null;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildFullLogFileName(item.id)),
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || AUDIT_LOGS_FAILED_DOWNLOAD_MESSAGE),
      });
  }

  protected openAuditLogDetail(item: AuditLogItem): void {
    if (!this.isEditEvent(item.event)) {
      return;
    }

    this.selectedLogItem = item;
    this.selectedLogDetail = null;
    this.selectedLogChanges = [];
    this.detailModalErrorMessage = '';
    this.isDetailModalOpen = true;
    this.isDetailLoading = true;

    this.httpSvc
      .requestCall(AuthEndPoints.GET_ACTIVITY_LOG_DETAIL, ApiMethod.GET, `?id=${item.id}`)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isDetailLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          const detail = this.extractLogDetail(res);
          if (!detail) {
            this.detailModalErrorMessage = 'Audit log changes not found.';
            this.selectedLogDetail = null;
            this.selectedLogChanges = [];
            return;
          }

          this.selectedLogDetail = detail;
          this.selectedLogChanges = this.extractChangedFields(detail.updated_json);
          if (!this.selectedLogChanges.length) {
            this.detailModalErrorMessage = 'No changes found for this log entry.';
          }
        },
        error: () => {
          this.detailModalErrorMessage = AUDIT_LOGS_FAILED_DETAIL_MESSAGE;
          this.selectedLogDetail = null;
          this.selectedLogChanges = [];
        },
      });
  }

  protected closeAuditLogDetailModal(): void {
    this.isDetailModalOpen = false;
    this.isDetailLoading = false;
    this.selectedLogItem = null;
    this.selectedLogDetail = null;
    this.selectedLogChanges = [];
    this.detailModalErrorMessage = '';
  }

  protected resetFilters(): void {
    this.filterForm.reset(
      {
        search: '',
        module_name: '',
        event: AUDIT_LOGS_ALL_FILTER_VALUE,
        from_date: '',
        to_date: '',
        page_size: this.pageSizeControl.value ?? 10,
      },
      { emitEvent: false },
    );
    this.loadAuditLogs(1);
  }

  protected trackByLogId(_index: number, item: AuditLogItem): number {
    return item.id;
  }

  protected trackByChangeKey(_index: number, item: AuditLogChangeItem): string {
    return item.key;
  }

  protected canShowPagination(): boolean {
    return this.pagination.total_records > 0 || this.pagination.total_pages > 0;
  }

  protected getPaginationLabel(): string {
    if (!this.pagination.total_records) {
      return AUDIT_LOGS_NO_RECORDS_LABEL;
    }

    const start = (this.pagination.page_no - 1) * this.pagination.page_size + 1;
    const end = Math.min(this.pagination.page_no * this.pagination.page_size, this.pagination.total_records);
    return `${AUDIT_LOGS_SHOWING_RESULTS_LABEL} ${start} to ${end} of ${this.pagination.total_records} ${AUDIT_LOGS_RESULTS_LABEL}`;
  }

  protected getPaginationItems(): number[] {
    const totalPages = this.pagination.total_pages;
    if (!totalPages) {
      return [];
    }

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const currentPage = this.pagination.page_no;
    const pages: number[] = [];
    const addPage = (pageNo: number): void => {
      if (pageNo >= 1 && pageNo <= totalPages && !pages.includes(pageNo)) {
        pages.push(pageNo);
      }
    };

    addPage(1);
    const windowStart = Math.max(2, currentPage - 1);
    const windowEnd = Math.min(totalPages - 1, currentPage + 1);
    if (windowStart > 2) {
      pages.push(-1);
    }
    for (let page = windowStart; page <= windowEnd; page += 1) {
      addPage(page);
    }
    if (windowEnd < totalPages - 1) {
      pages.push(-1);
    }
    addPage(totalPages);
    return pages;
  }

  protected goToPreviousPage(): void {
    this.goToPage(this.pagination.page_no - 1);
  }

  protected goToNextPage(): void {
    this.goToPage(this.pagination.page_no + 1);
  }

  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages) {
      return;
    }

    this.pagination = { ...this.pagination, page_no: pageNo };
    this.loadAuditLogs(pageNo);
  }

  protected getEventLabel(event: string | null | undefined): string {
    const normalizedEvent = this.normalizeEventCode(event);
    if (normalizedEvent === 'get') {
      return 'Fetch';
    }
    if (normalizedEvent === 'post') {
      return 'Add';
    }
    if (normalizedEvent === 'patch' || normalizedEvent === 'put') {
      return 'Edit';
    }
    if (normalizedEvent === 'delete') {
      return 'Delete';
    }

    return this.toTitleCase(this.normalizeDisplayText(event));
  }

  protected formatModuleName(moduleName: string): string {
    return this.toTitleCase(moduleName?.replace(/[-_]/g, ' ') ?? '-');
  }

  protected formatText(value: AuditLogCellValue): string {
    const normalizedValue = this.normalizeDisplayText(value);
    return normalizedValue || '-';
  }

  protected formatDescription(item: AuditLogItem): string {
    const description = this.normalizeDisplayText(item.description);
    if (!description) {
      return '-';
    }

    return this.maskEmailAddresses(description, item.user_name?.trim() || 'User');
  }

  protected isEditEvent(event: string | null | undefined): boolean {
    return this.normalizeEventCode(event) === 'patch';
  }

  protected getDetailTitle(): string {
    if (!this.selectedLogDetail) {
      return 'Audit Log Changes';
    }

    const eventLabel = this.getEventLabel(this.selectedLogDetail.event);
    const moduleName = this.formatModuleName(this.selectedLogDetail.module_name);
    return `${eventLabel} Changes - ${moduleName}`;
  }

  protected getDetailSubtitle(): string {
    if (!this.selectedLogDetail) {
      return 'Only changed values are shown here.';
    }

    const performedBy = this.formatText(this.selectedLogDetail.user_name);
    const performedOn = this.formatText(this.selectedLogDetail.updated_at || this.selectedLogDetail.created_at);
    return `${performedBy} • ${performedOn}`;
  }

  protected formatDiffValue(value: string): string {
    return value || '-';
  }

  private buildQueryString(params: AuditLogsQueryParams): string {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || value === AUDIT_LOGS_ALL_FILTER_VALUE) {
        return;
      }
      queryParams.set(key, String(value));
    });

    const query = queryParams.toString();
    return query ? `?${query}` : '';
  }

  private extractPagination(value: unknown): AuditLogsPagination | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const response = value as { pagination?: unknown; value?: { pagination?: unknown } };
    const pagination = response.pagination ?? response.value?.pagination;
    if (!pagination || typeof pagination !== 'object') {
      return null;
    }

    const record = pagination as Record<string, unknown>;
    return {
      page_no: Number(record['page_no'] ?? 1),
      page_size: Number(record['page_size'] ?? 10),
      total_records: Number(record['total_records'] ?? 0),
      total_pages: Number(record['total_pages'] ?? 0),
    };
  }

  private buildAuditLogsFileName(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `audit_logs_${year}${month}${day}_${hours}${minutes}${seconds}.csv`;
  }

  private buildFullLogFileName(logId: number): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `audit_log_${logId}_${year}${month}${day}_${hours}${minutes}${seconds}.txt`;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  }

  private getApiErrorMessage(error: unknown): string | null {
    if (!error || typeof error !== 'object') {
      return null;
    }

    const errorObj = error as { error?: unknown; message?: string };
    const payload = errorObj.error as { msg?: unknown; message?: unknown; detail?: unknown } | undefined;
    const message = payload?.msg ?? payload?.message ?? errorObj.message ?? payload?.detail;
    return typeof message === 'string' && message.trim() ? message.trim() : null;
  }

  private normalizeEventCode(event: string | null | undefined): string {
    const value = this.normalizeDisplayText(event).toLowerCase();
    if (value === 'fetch') {
      return 'get';
    }
    if (value === 'add') {
      return 'post';
    }
    if (value === 'edit') {
      return 'patch';
    }
    if (value === 'delete') {
      return 'delete';
    }
    return value;
  }

  private normalizeDisplayText(value: AuditLogCellValue): string {
    if (value === null || value === undefined) {
      return '';
    }

    const normalizedValue = typeof value === 'string' ? value.trim() : String(value).trim();
    return normalizedValue;
  }

  private toTitleCase(value: string): string {
    return value
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private extractLogDetail(response: unknown): AuditLogDetail | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const payload = response as { value?: unknown; data?: unknown; updated_json?: unknown };
    const candidate = payload.value ?? payload.data ?? response;
    if (!candidate || typeof candidate !== 'object') {
      return null;
    }

    const detail = candidate as AuditLogDetail;
    return {
      ...detail,
      updated_json: this.normalizeJsonValue(detail.updated_json),
      properties: detail.properties || {},
    };
  }

  private normalizeJsonValue(value: unknown): unknown {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return null;
      }

      try {
        return JSON.parse(trimmedValue);
      } catch {
        return value;
      }
    }

    return value;
  }

  private extractChangedFields(updatedJson: unknown): AuditLogChangeItem[] {
    const changes: AuditLogChangeItem[] = [];
    const source = this.normalizeJsonValue(updatedJson);

    if (!source || typeof source !== 'object') {
      return changes;
    }

    if (Array.isArray(source)) {
      source.forEach((entry, index) => {
        if (!entry || typeof entry !== 'object') {
          return;
        }

        const record = entry as Record<string, unknown>;
        const rawKey = record['key'] ?? record['field'] ?? record['name'] ?? `Field ${index + 1}`;
        const key = this.normalizeDisplayText(this.stringifyDiffValue(rawKey));
        changes.push({
          key: this.toTitleCase(key.replace(/[_-]/g, ' ')),
          old_value: this.stringifyDiffValue(record['old'] ?? record['old_value'] ?? record['previous'] ?? record['before']),
          new_value: this.stringifyDiffValue(record['new'] ?? record['new_value'] ?? record['after']),
        });
      });
      return changes.filter((change) => change.key !== '' || change.old_value !== '-' || change.new_value !== '-');
    }

    const record = source as Record<string, unknown>;
    Object.entries(record).forEach(([key, value]) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const nestedRecord = value as Record<string, unknown>;
        if ('old' in nestedRecord || 'new' in nestedRecord || 'old_value' in nestedRecord || 'new_value' in nestedRecord || 'previous' in nestedRecord || 'after' in nestedRecord) {
          changes.push({
            key: this.toTitleCase(key.replace(/[_-]/g, ' ')),
            old_value: this.stringifyDiffValue(nestedRecord['old'] ?? nestedRecord['old_value'] ?? nestedRecord['previous'] ?? nestedRecord['before']),
            new_value: this.stringifyDiffValue(nestedRecord['new'] ?? nestedRecord['new_value'] ?? nestedRecord['after']),
          });
          return;
        }
      }

      changes.push({
        key: this.toTitleCase(key.replace(/[_-]/g, ' ')),
        old_value: '-',
        new_value: this.stringifyDiffValue(value),
      });
    });

    return changes.filter((change) => change.key !== '' || change.old_value !== '-' || change.new_value !== '-');
  }

  private stringifyDiffValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      return trimmedValue || '-';
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return '[Unserializable value]';
    }
  }

  private maskEmailAddresses(value: string, replacement: string): string {
    const fallbackReplacement = replacement || 'User';
    let result = '';
    let index = 0;

    while (index < value.length) {
      const atIndex = value.indexOf('@', index);
      if (atIndex === -1) {
        result += value.slice(index);
        break;
      }

      const localStart = this.findEmailLocalStart(value, atIndex - 1);
      const domainEnd = this.findEmailDomainEnd(value, atIndex + 1);
      if (localStart === atIndex || domainEnd <= atIndex + 1 || !this.isValidEmailCandidate(value.slice(localStart, domainEnd))) {
        result += value.slice(index, atIndex + 1);
        index = atIndex + 1;
        continue;
      }

      result += value.slice(index, localStart) + fallbackReplacement;
      index = domainEnd;
    }

    return result;
  }

  private findEmailLocalStart(value: string, index: number): number {
    let current = index;
    while (current >= 0 && this.isEmailLocalChar(value.charAt(current))) {
      current -= 1;
    }
    return current + 1;
  }

  private findEmailDomainEnd(value: string, index: number): number {
    let current = index;
    let hasDot = false;

    while (current < value.length && this.isEmailDomainChar(value.charAt(current))) {
      if (value.charAt(current) === '.') {
        hasDot = true;
      }
      current += 1;
    }

    return hasDot ? current : index;
  }

  private isValidEmailCandidate(candidate: string): boolean {
    const atIndex = candidate.indexOf('@');
    if (atIndex <= 0 || atIndex === candidate.length - 1) {
      return false;
    }

    const localPart = candidate.slice(0, atIndex);
    const domainPart = candidate.slice(atIndex + 1);
    const dotIndex = domainPart.lastIndexOf('.');
    if (!localPart || !domainPart || dotIndex <= 0 || dotIndex === domainPart.length - 1) {
      return false;
    }

    const tld = domainPart.slice(dotIndex + 1);
    return tld.length >= 2 && this.isEmailLocalSegmentValid(localPart) && this.isEmailDomainSegmentValid(domainPart);
  }

  private isEmailLocalSegmentValid(value: string): boolean {
    for (let index = 0; index < value.length; index += 1) {
      if (!this.isEmailLocalChar(value.charAt(index))) {
        return false;
      }
    }
    return true;
  }

  private isEmailDomainSegmentValid(value: string): boolean {
    let dotSeen = false;
    for (let index = 0; index < value.length; index += 1) {
      const char = value.charAt(index);
      if (char === '.') {
        dotSeen = true;
        continue;
      }
      if (!this.isEmailDomainChar(char)) {
        return false;
      }
    }
    return dotSeen;
  }

  private isEmailLocalChar(char: string): boolean {
    const code = char.charCodeAt(0);
    return (
      (code >= 48 && code <= 57) ||
      (code >= 65 && code <= 90) ||
      (code >= 97 && code <= 122) ||
      char === '.' ||
      char === '_' ||
      char === '%' ||
      char === '+' ||
      char === '-'
    );
  }

  private isEmailDomainChar(char: string): boolean {
    const code = char.charCodeAt(0);
    return (
      (code >= 48 && code <= 57) ||
      (code >= 65 && code <= 90) ||
      (code >= 97 && code <= 122) ||
      char === '.' ||
      char === '-'
    );
  }
}
